import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription, map, skipWhile, switchMap, take } from 'rxjs';
import { TableLazyLoadEvent } from 'primeng/table';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { ALLOWED_FILE_TYPES, ALLOWED_IMAGE_TYPES, EFileType, MAX_UPLOAD_SIZE, USER_FILES_DEFAULT_SORT_COLUMN, USER_FILES_DEFAULT_SORT_COLUMN_DIRECTION, USER_FILES_PAGE_DEFAULT_SIZE, USER_FILES_PAGE_SIZE_OPTIONS } from 'src/app/conf/user-files';
import { UserFile } from 'src/app/model/user-file';
import * as UserFileActions from 'src/app/store/user-file/user-file.actions';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { pageSortChange } from 'src/app/services/table';
import { Site } from 'src/app/model/site';
import { selectLoading, selectPaging, selectUserFiles } from 'src/app/store/user-file/user-file.selector';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { FormGroup } from '@angular/forms';
import { Tooltip } from 'primeng/tooltip';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import * as NotificationsActions from 'src/app/store/notifications/notifications.actions';
import { User } from 'src/app/model/user';
import { selectCurrentTechnician } from 'src/app/store/user/user.selector';
import { StandardPageLayout } from '../../stdlayoutlib/classes.stdlayoutlib';

@Component({
	templateUrl: './files.dialog.component.html',
	providers: [ConfirmationService, DialogService]
})

export class FilesDialogComponent implements OnInit, OnDestroy {
	@ViewChild('fileUpload') fileUpload: any;

	labelFile: string;

	filesLoading$: Observable<boolean | undefined>;
	filesLoadingSub: Subscription | undefined;
	files$: Observable<UserFile[] | undefined>;
	filesSub: Subscription | undefined;
	filesPage$: Observable<ItemsPagination | undefined>;
	filesPageSub: Subscription | undefined;
	currentTechnician$: Observable<User | undefined>;
	currentTechnicianSub: Subscription | undefined;

	filesLoading: boolean;
	files: UserFile[];

	image: string | ArrayBuffer;
	messageErrorUpload = "";
	@ViewChild('imageInput', { static: false }) imageInput: ElementRef;

	totalRecords = 0;

	// Upload File
	fileToUpload: File;
	uploadedFile: string | ArrayBuffer;

	maxUploadSize = MAX_UPLOAD_SIZE;

	paging: PaginationParams = {
		size: USER_FILES_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: USER_FILES_DEFAULT_SORT_COLUMN,
		order: USER_FILES_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSizeOptions = USER_FILES_PAGE_SIZE_OPTIONS;
	pageSize = USER_FILES_PAGE_DEFAULT_SIZE;
	resultLength: number;

	siteForm: FormGroup;
	site: Site;
	owner: boolean;
	technician: boolean;

	sort: any;
	paginator: any;
	currentTechnician?: User;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	showUploadButton = false;
	reuploadButton: boolean;

	screenWidth: number;

	@ViewChild(Tooltip) tooltip!: Tooltip;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private http: HttpClient,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
	) {
		if (config.data !== undefined && config.data.site !== undefined) {
			this.site = config.data.site;
		}
		if (config.data !== undefined && config.data.owner !== undefined) {
			this.owner = config.data.owner;
		}
		if (config.data !== undefined && config.data.technician !== undefined) {
			this.technician = config.data.technician;
		}
		this.initSelectors();
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.getUserFiles();
		this.onResize();
	}

	initSelectors(): void {
		this.filesLoading$ = this.store.select(selectLoading);
		this.files$ = this.store.select(selectUserFiles);
		this.filesPage$ = this.store.select(selectPaging);
		this.currentTechnician$ = this.store.select(selectCurrentTechnician);

		this.uploadInit();
	}

	showHideTooltip() {
		this.tooltip.activate();
	}

	subscribeAll(): void {
		this.currentTechnicianSub = this.currentTechnician$.subscribe(
			(currentTechnician) => {
				this.currentTechnician = currentTechnician;
			}
		);
		this.filesLoadingSub = this.filesLoading$?.subscribe(
			(filesLoading) => {
				this.filesLoading = filesLoading;
			}
		);
		this.filesSub = this.files$?.subscribe(
			(files) => {
				this.files = files;
			}
		);
		this.filesPageSub = this.filesPage$?.subscribe(
			paging => {
				if (paging !== null && paging !== undefined) {
					this.totalRecords = paging.TotalCount;
				} else {
					this.totalRecords = 0;
				}
			}
		);
	}

	ngOnDestroy(): void {
		this.filesLoadingSub?.unsubscribe();
		this.filesSub?.unsubscribe();
		this.filesPageSub?.unsubscribe();
		this.currentTechnicianSub?.unsubscribe();
	}

	closeDialog(): void {
		this.ref.close();
	}

	getUserFiles(): void {
		this.store.dispatch(UserFileActions.getUserFiles({
			typeId: EFileType.USER_SITE_FILE,
			siteId: this.site.Id,
			noPagination: false,
			paging: this.paging
		}));
	}

	loadFiles(event: TableLazyLoadEvent) {
		this.filesLoading = true;

		this.paging = pageSortChange(this.paging, event);

		this.getUserFiles();
	}

	delete(file: UserFile): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_FILE'),
			message: this.translate.instant('DELETE_FILE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(UserFileActions.deleteUserFile({ userFile: file }));
			},
			reject: () => {
			}
		});
	}

	download(userFile: UserFile) {
		this.store.dispatch(UserFileActions.getUserFileContent({ userFile }));
		this.store.select('userFile', 'userFilesContents').pipe(
			map((ufcs) => ufcs.find((ucf) => ucf.Id === userFile.Id)),
			skipWhile(f => !f),
			take(1),
			switchMap((file) => this.http.get(file.Content, { responseType: 'blob' }).pipe(
				map(val => {
					const url = URL.createObjectURL(val);
					this.downloadUrl(url, file.Name);
					URL.revokeObjectURL(url);
					this.store.dispatch(UserFileActions.clearFileContent({ userFile: file }));
				})
			))
		).subscribe();
	}

	downloadUrl(url: string, fileName: string) {
		const a: any = document.createElement('a');
		a.href = url;
		a.download = fileName;
		document.body.appendChild(a);
		a.style = 'display: none';
		a.click();
		a.remove();
	}

	handleFileInput(event) {
		if (event.currentFiles.length > 0) {
			this.fileToUpload = event.currentFiles[0];

			if (!ALLOWED_FILE_TYPES.includes(this.fileToUpload.type)) {
				const notification = new Notification("FILE_NOT_COMPATIBLE", this.translate.instant("MAX_UPLOAD_SIZE_1") + " 900KB", NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.fileToUpload = null;
				this.reuploadButton = true;
			} else if (this.fileToUpload.size > MAX_UPLOAD_SIZE) {
				const notification = new Notification("FILE_TOO_BIG", this.translate.instant("MAX_UPLOAD_SIZE_1") + " 900KB", NotificationLevel.Error, '');
				this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));

				this.fileToUpload = null;
				this.reuploadButton = true;
			} else {
				const myReader: FileReader = new FileReader();
				myReader.onloadend = () => {
					this.image = myReader.result;
				};
				myReader.readAsDataURL(this.fileToUpload);
				this.labelFile = this.fileToUpload.name;
				this.showUploadButton = true;
			}
		}
	}

	onUploadClick(): void {
		const file: UserFile = {
			// Id: null,
			Type: EFileType.USER_SITE_FILE, // site related file
			Name: this.fileToUpload.name,
			SiteId: this.site.Id,
			Content: btoa(this.image.toString()),
			MimeType: this.fileToUpload.type,
		};
		this.store.dispatch(UserFileActions.addUserFile({ userFile: file }));
		this.fileToUpload = null;
		this.showUploadButton = false;

		const notification = new Notification("SUCCESS", this.translate.instant("IMAGE_FILE_UPLOADED_SUCCESS"), NotificationLevel.Success, '');
		this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
		this.reuploadButton = true;
	}

	uploadInit() {
		this.reuploadButton = false;
	}

	setReupload() {
		this.reuploadButton = false;
		this.fileToUpload = null;
	}

	impersonating(): boolean {
		return !!this.currentTechnician;
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize(): void {
		this.screenWidth = window.innerWidth;
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-20rem');
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}